/**
 * Pantalla principal del Radar.
 * Muestra usuarios cercanos basados en la ubicación y preferencias.
 * Optimizada con hooks personalizados y sistema de cache para drawers.
 * Integra todos los componentes del módulo Radar.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useRadarState } from './hooks/useRadarState';
import RadarHeader from './components/RadarHeader';
import RadarCounter from './components/RadarCounter';
import { RadarGrid } from './components/RadarGrid';
import { PreferencesModal } from './components/PreferencesModal';

function RadarContent() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Estados para controlar visibilidad de modales
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false);

  // Memoizar callbacks para evitar re-renders de componentes hijos
  const handleOpenProfile = useCallback(() => {
    navigate('/mi-cuenta/editar');
  }, [navigate]);

  const handleOpenPreferences = useCallback(() => {
    setPreferencesModalOpen(true);
  }, []);

  // Navega a la pantalla de perfil de amigo usando location.state para no exponer UUID
  const handleOpenFriendProfile = useCallback((friendId) => {
    navigate('/friend/profile', { state: { friendId } });
  }, [navigate]);

  // Hook personalizado que gestiona todo el estado y lógica del radar
  const {
    loadingUsers,
    refreshing,
    usersList,
    feedbackMessage,
    totalPersonas,
    nearbyUsersIds,
    location,
    loadRadarData,
    handleRefresh,
  } = useRadarState(user);

  // Ref para tracking de carga inicial y evitar duplicados
  const hasLoadedRef = React.useRef(false);

  // Efecto único que maneja carga inicial y cambios de autenticación
  React.useEffect(() => {
    console.log('[RadarContent] Estado de autenticación:', !!user?.id);
    
    // Solo cargar si hay usuario autenticado
    if (!user?.id) {
      hasLoadedRef.current = false;
      return;
    }

    // Evitar carga duplicada en el montaje inicial
    if (hasLoadedRef.current) {
      console.log('[RadarContent] Ya se cargaron datos, saltando carga duplicada');
      return;
    }

    console.log('[RadarContent] Cargando datos iniciales del radar');
    loadRadarData();
    hasLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Solo dependemos de user?.id, no de loadRadarData

  return (
    <div className="">
      {/* Header con avatar y controles */}
      <RadarHeader
        onOpenProfile={handleOpenProfile}
        onOpenPreferences={handleOpenPreferences}
        onRefresh={handleRefresh}
      />

      {/* Contador de personas encontradas */}
      <RadarCounter totalPersonas={totalPersonas} />

      {/* Lista de usuarios con refresh */}
      <RadarGrid
        usersList={usersList}
        loadingUsers={loadingUsers}
        refreshing={refreshing}
        feedbackMessage={feedbackMessage}
        onRefresh={handleRefresh}
        nearbyUsersIds={nearbyUsersIds}
        location={location}
        onOpenFriendProfile={handleOpenFriendProfile}
      />

      {/* Modales de perfil y preferencias */}
      
      <PreferencesModal 
        isOpen={preferencesModalOpen} 
        onClose={() => setPreferencesModalOpen(false)} 
      />
    </div>
  );
}

/**
 * Componente wrapper principal con memo para optimizar re-renders
 */
export default React.memo(function Radar() {
  return <RadarContent />;
});
